import React, { useState } from 'react';
import { Autocomplete, CircularProgress, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { MutationError } from '@elementary/common-ui/types/mutationTypes';
import { ReactComponent as ArrowLeft } from '@elementary/common-ui/assets/arrow-left.svg';
import {
  Alert,
  Button,
  FlexColumn,
  IconButton,
  Loading,
  TextField,
} from '@elementary/common-ui/components';
import useAlertDestinations from 'shared/hooks/useAlertDestinations';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as api from 'shared/services/settingsApi';
import TestMessageSection from './TestMessageSection';

type Props = { onSuccess?: () => void; onReset?: () => void };

const PagerDutyTestSection = ({ onSuccess, onReset }: Props) => {
  const [selectedService, setSelectedService] = useState<string>('');
  const [customMessage, setCustomMessage] = useState<string>('');
  const [manualServiceEntry, setManualServiceEntry] = useState(false);

  const { openSnackbar } = useSnackbar();

  const {
    alertDestinationOptions,
    alertDestinationsLoading,
    alertDestinationsError,
  } = useAlertDestinations();

  const services = alertDestinationOptions?.pager_duty?.options || [];
  const hasPagerDutyIntegration = alertDestinationOptions?.pager_duty?.id;
  const servicesLoadedSuccessfully = services.length > 0;

  const defaultMessage =
    'This is a test incident from Elementary Data to verify your PagerDuty integration is working correctly.';

  const testMutation = useMutation(
    async (data: { service_id: string; message?: string }) => {
      const response = await api.sendPagerDutyTestIncident(data);
      return response;
    },
    {
      onSuccess: (response) => {
        if (response.success) {
          openSnackbar({
            message: 'Test incident sent successfully to PagerDuty!',
            variant: 'success',
          });
          onSuccess?.();
        } else {
          openSnackbar({
            message:
              response.message || 'Failed to send test incident to PagerDuty',
            variant: 'error',
          });
          onReset?.();
        }
      },
      onError: (error: MutationError) => {
        openSnackbar({
          message:
            error?.message ||
            error?.response?.data?.message ||
            'Failed to send test incident to PagerDuty',
          variant: 'error',
        });
      },
    },
  );

  const handleSendTest = () => {
    if (!selectedService) return;

    const message = customMessage.trim() || defaultMessage;
    testMutation.mutate({
      service_id: selectedService,
      message,
    });
  };

  // Determine if we should show error message for missing integration
  const integrationErrorMessage =
    !hasPagerDutyIntegration && !alertDestinationsLoading
      ? 'PagerDuty integration is not properly connected. Please set up your PagerDuty integration first.'
      : null;

  const autocompleteSelectedVal =
    services.find((service) => service.value === selectedService) || null;

  return (
    <FlexColumn sx={{ gap: 3 }}>
      <Typography>
        Send a test incident to verify your PagerDuty integration is working
        correctly.
      </Typography>

      <Alert severity="warning">
        <strong>Important:</strong> This will create a real incident in your
        PagerDuty account. You will need to manually resolve or close the
        incident after testing.
      </Alert>

      {integrationErrorMessage && (
        <Alert severity="error">{integrationErrorMessage}</Alert>
      )}

      <FlexColumn sx={{ gap: 2 }}>
        {alertDestinationsLoading && <Loading />}
        {hasPagerDutyIntegration && !!alertDestinationsError && (
          <Alert
            severity="error"
            action={
              <Button
                color="secondary"
                onClick={() => setManualServiceEntry(true)}
              >
                Enter Manually
              </Button>
            }
          >
            Failed to load PagerDuty services. You can enter a service ID
            manually or try refreshing the page.
          </Alert>
        )}

        {hasPagerDutyIntegration && (
          <>
            {!manualServiceEntry && !alertDestinationsError ? (
              <Autocomplete
                options={services}
                getOptionLabel={(option) => option.label}
                value={autocompleteSelectedVal}
                onChange={(_, newValue) =>
                  setSelectedService(newValue?.value || '')
                }
                loading={alertDestinationsLoading}
                disabled={testMutation.isLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select PagerDuty service"
                    placeholder="Choose a service to send the test incident"
                    required
                    size="large"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {alertDestinationsLoading && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                    helperText={
                      !alertDestinationsLoading &&
                      !servicesLoadedSuccessfully &&
                      !alertDestinationsError
                        ? 'No services found. You can enter a service ID manually below.'
                        : undefined
                    }
                  />
                )}
              />
            ) : (
              <TextField
                label="PagerDuty Service ID"
                value={selectedService}
                onChange={(e) => setSelectedService(e.target.value)}
                placeholder="Enter your PagerDuty service ID (e.g., P1DTXQY)"
                required
                disabled={testMutation.isLoading}
                helperText="You can find service IDs in your PagerDuty dashboard under Services"
                size="large"
              />
            )}

            {!manualServiceEntry &&
              hasPagerDutyIntegration &&
              !alertDestinationsError && (
                <Button
                  variant="text"
                  onClick={() => setManualServiceEntry(true)}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Enter service ID manually instead
                </Button>
              )}

            {manualServiceEntry && (
              <IconButton
                size="small"
                onClick={() => {
                  setManualServiceEntry(false);
                  setSelectedService('');
                }}
                sx={{ alignSelf: 'flex-start', gap: 1, padding: 1 }}
              >
                <ArrowLeft />
                <Typography sx={{ color: 'content.secondary' }}>
                  Back to service selection
                </Typography>
              </IconButton>
            )}
          </>
        )}
      </FlexColumn>

      <TestMessageSection
        customMessage={customMessage}
        onCustomMessageChange={setCustomMessage}
        defaultMessage={defaultMessage}
        onSendTest={handleSendTest}
        isLoading={testMutation.isLoading}
        disabled={!autocompleteSelectedVal}
        messageLabel="Test incident message (optional)"
      />
    </FlexColumn>
  );
};

export default PagerDutyTestSection;
